/* ==========================================================================
   LimeLight Merch — theme.js
   Behaviour carried over from the approved standalone build:
     1. scroll reveal for [data-anim]
     2. mobile nav drawer + submenu accordions
     3. hero side-graphic parallax
   Changes vs the standalone file: each initialiser is idempotent and re-runs on
   shopify:section:load, so the theme editor can swap a section in without
   double-binding listeners or stacking a second backdrop.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- 1. Scroll reveal ------------------------------------------------
     [data-anim] elements start at opacity:0 in theme.css, so this observer is
     required for content to appear. */
  var revealObserver = null;

  function initReveal(root) {
    var els = root.querySelectorAll('[data-anim]:not([data-anim-bound])');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-anim-bound', '');
        el.classList.add('is-in');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            revealObserver.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }

    els.forEach(function (el) {
      el.setAttribute('data-anim-bound', '');
      revealObserver.observe(el);
    });
  }

  /* In the theme editor, a section rendered off-screen never intersects, which
     would leave it invisible. Reveal anything already in view immediately. */
  function revealVisible(root) {
    if (!window.Shopify || !window.Shopify.designMode) return;
    root.querySelectorAll('[data-anim]').forEach(function (el) {
      el.classList.add('is-in');
    });
  }

  /* ---- 2. Mobile nav drawer ------------------------------------------- */
  var backdrop = null;

  function initNav(root) {
    var toggle = root.querySelector('.nav-toggle');
    var nav = root.querySelector('#primary-nav');
    var close = root.querySelector('.nav__close');
    if (!toggle || !nav || toggle.hasAttribute('data-nav-bound')) return;
    toggle.setAttribute('data-nav-bound', '');

    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'nav-backdrop';
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
    }

    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      backdrop.hidden = !open;
      document.documentElement.classList.toggle('nav-lock', open);
      toggle.setAttribute('aria-expanded', String(open));
    }

    toggle.addEventListener('click', function () {
      setOpen(!nav.classList.contains('is-open'));
    });
    if (close) close.addEventListener('click', function () { setOpen(false); });
    backdrop.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setOpen(false);
    });

    /* Submenu accordions on mobile */
    root.querySelectorAll('.nav__toggle-link').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var li = btn.parentElement;
        var open = li.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* ---- 3. Hero side-graphic parallax ---------------------------------
     Pulls the half-circles outward on scroll. */
  var parallaxBound = false;

  function initParallax(root) {
    var hero = root.querySelector('.hero--shop');
    if (!hero) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (parallaxBound) return;
    parallaxBound = true;

    var ticking = false;

    function update() {
      var left = document.querySelector('.side-graphic--left img');
      var right = document.querySelector('.side-graphic--right img');
      ticking = false;
      if (!left || !right) return;
      var shift = Math.min(window.scrollY * 0.18, 160);
      left.style.transform = 'scaleX(-1) translateX(' + shift + 'px)';
      right.style.transform = 'translateX(' + shift + 'px)';
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---- 4. Product page: variant picker + gallery ----------------------
     One <select> per option; this resolves the combination to a variant and
     updates the hidden id, price, stock line and button. If this never runs,
     the hidden id still holds the first available variant, so add-to-cart
     keeps working — it just stops reacting to the selects. */
  function initProduct(root) {
    var pdp = root.querySelector('[data-pdp]');
    if (!pdp || pdp.hasAttribute('data-pdp-bound')) return;

    var section = pdp.closest('section') || document;
    var dataEl = section.querySelector('[data-pdp-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; /* leave the server-rendered state alone */
    }
    if (!variants || !variants.length) return;

    pdp.setAttribute('data-pdp-bound', '');

    var selects = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-option]'));
    var idInput = pdp.querySelector('[data-pdp-id]');
    var priceEl = pdp.querySelector('[data-pdp-price]');
    var stockEl = pdp.querySelector('[data-pdp-stock]');
    var submit = pdp.querySelector('[data-pdp-submit]');
    var submitText = pdp.querySelector('[data-pdp-submit-text]');
    var inStockText = stockEl ? stockEl.textContent.trim() : '';

    function match() {
      var chosen = selects.map(function (s) { return s.value; });
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < chosen.length; j++) {
          if (v.options[j] !== chosen[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    function update() {
      var v = match();

      if (!v) {
        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = 'Unavailable';
        if (stockEl) {
          stockEl.textContent = 'That combination is not available';
          stockEl.classList.add('pdp__stock--out');
        }
        return;
      }

      if (idInput) idInput.value = v.id;

      if (priceEl) {
        if (v.on_sale) {
          priceEl.innerHTML = '<del></del><span class="pdp__now"></span> <span class="pdp__badge">Sale</span>';
          priceEl.querySelector('del').textContent = v.compare_at;
          priceEl.querySelector('.pdp__now').textContent = v.price;
        } else {
          priceEl.textContent = v.price;
        }
      }

      if (stockEl) {
        stockEl.textContent = v.available ? inStockText : 'Sold out';
        stockEl.classList.toggle('pdp__stock--out', !v.available);
      }

      if (submit) submit.disabled = !v.available;
      if (submitText) submitText.textContent = v.available ? 'Add to cart' : 'Sold out';

      /* keep the URL shareable without reloading */
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    selects.forEach(function (s) { s.addEventListener('change', update); });

    /* Gallery thumbs swap the main image. */
    var main = pdp.querySelector('[data-pdp-main]');
    pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!main) return;
        main.removeAttribute('srcset');
        main.src = btn.getAttribute('data-full');
        pdp.querySelectorAll('[data-pdp-thumb]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
      });
    });
  }

  function init(root) {
    root = root || document;
    initReveal(root);
    revealVisible(root);
    initNav(root);
    initParallax(root);
    initProduct(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
